/**
 * SENVÈ Neck Device ritual selector (Single / Duo / Trio + Gua Sha add-on).
 * Adds real line items via /cart/add.js and hands the response to Dawn's
 * own cart-drawer (renderContents) - reuses the existing cart infra instead
 * of building a parallel one.
 */
(() => {
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  class SenveRitual {
    constructor(root) {
      this.root = root;
      this.devicePrice = parseInt(root.dataset.devicePrice, 10);
      this.deviceVariantId = root.dataset.deviceVariantId;
      this.totals = {
        single: parseInt(root.dataset.singleTotal, 10),
        duo: parseInt(root.dataset.duoTotal, 10),
        trio: parseInt(root.dataset.trioTotal, 10),
      };
      this.addonVariantId = root.dataset.addonVariantId || null;
      this.addonPrice = root.dataset.addonPrice ? parseInt(root.dataset.addonPrice, 10) : null;

      this.selected = 'single';
      this.addonAdded = false;

      this.optionButtons = Array.from(root.querySelectorAll('[data-ritual-option]'));
      this.addon = root.querySelector('[data-addon]');
      this.addonCheckbox = root.querySelector('[data-addon-checkbox]');
      this.totalEl = root.querySelector('[data-ritual-total]');
      this.addButton = root.querySelector('[data-ritual-add-to-cart]');
      this.addLabel = root.querySelector('[data-ritual-add-label]');
      this.errorEl = root.querySelector('[data-ritual-error]');

      this.optionButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.selectOption(btn.dataset.ritualOption));
      });

      if (this.addonCheckbox) {
        this.addonCheckbox.addEventListener('change', () => {
          this.addonAdded = this.addonCheckbox.checked;
          this.updateTotal();
        });
      }

      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.addToCart());
      }

      this.updateTotal();
    }

    selectOption(name) {
      this.selected = name;
      this.optionButtons.forEach((btn) => {
        const isSelected = btn.dataset.ritualOption === name;
        btn.classList.toggle('is-selected', isSelected);
        btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });
      this.updateTotal();
    }

    // The Gua Sha add-on has no discount of its own - it already runs a BOGO
    // promotion, and stacking a second mechanic on top would misstate what
    // checkout charges. It is always priced at its listed variant price.
    currentAddonLinePrice() {
      if (!this.addonAdded || !this.addonVariantId) return 0;
      return this.addonPrice || 0;
    }

    updateTotal() {
      const deviceTotal = this.totals[this.selected];
      const total = deviceTotal + this.currentAddonLinePrice();
      if (this.totalEl) this.totalEl.textContent = formatMoney(total);
      if (this.addLabel) this.addLabel.textContent = 'Add to Cart — ' + formatMoney(total);
    }

    setLoading(isLoading) {
      if (!this.addButton) return;
      this.addButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      this.addButton.classList.toggle('loading', isLoading);
      const spinner = this.addButton.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    addToCart() {
      if (this.addButton.getAttribute('aria-disabled') === 'true') return;

      this.clearError();
      this.setLoading(true);

      const items = [
        {
          id: this.deviceVariantId,
          quantity: parseInt(this.root.querySelector('.is-selected').dataset.qty, 10),
        },
      ];

      if (this.addonAdded && this.addonVariantId) {
        items.push({ id: this.addonVariantId, quantity: 1 });
      }

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { items };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          this.setLoading(false);
          if (response.status) {
            this.showError(response.description || response.message || 'Something went wrong. Please try again.');
            return;
          }
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(response);
          } else {
            window.location = window.routes ? window.routes.cart_url : '/cart';
          }
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'senve-ritual-selector',
              cartData: response,
            });
          }
        })
        .catch(() => {
          this.setLoading(false);
          this.showError('Something went wrong. Please try again.');
        });
    }
  }

  function init() {
    document.querySelectorAll('[data-senve-ritual]').forEach((root) => new SenveRitual(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
